/**
 * Wave-Viz 5：教务交付进度看板（V-FE-academic）
 *
 * 调 GET /academic/delivery-board，展示各班型学员完成度分布 + 逾期任务数。
 * 端点契约：{ byClassType:[{classType,total,completed,avgProgress}], overdueTasks }
 *   - byClassType：各班型（启航/特训/孵化/创业/入企）学员数 total、已完成 completed、平均进度 avgProgress(0~1 代理值)
 *   - overdueTasks：逾期任务总数（约定口径=任务日期早于今天且未完成）
 *
 * 仅渲染，权限/可见性走宿主页面 DashboardPage 的 academic:view（App.tsx RouteGuard）。
 * 复用 ui.tsx 原语（HomeCard/StatCard/Pill/EmptyHint），严格走 var(--*) token。
 */
import { useQuery } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { HomeCard, StatCard, Pill, EmptyHint } from '../ui';

// 完成度是阶段序号代理值、非真实学习完成率——标题用「当前进度」措辞，避免误导（同漏斗快照免责口径）
type ClassTypeRow = {
  classType: string;
  total: number;
  completed: number;
  avgProgress: number; // 0~1
};

type DeliveryBoardData = {
  byClassType: ClassTypeRow[];
  overdueTasks: number;
};

export function AcademicDeliveryBoard() {
  const { data, isLoading } = useQuery<DeliveryBoardData>({
    queryKey: ['academic-delivery-board'],
    queryFn: () => $.get('/academic/delivery-board'),
  });

  const rows = data?.byClassType ?? [];
  const overdue = data?.overdueTasks ?? 0;
  // 全班型学员合计（用于「逾期任务」卡片副信息参考）
  const totalStudents = rows.reduce((a, r) => a + (r.total ?? 0), 0);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16, marginTop: 16 }}>
      {/* 各班型学员完成度分布 */}
      <HomeCard title={<>各班型交付进度（按班型）<span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400, marginLeft: 6 }}>进度=当前阶段代理值·非真实学习完成率</span></>}>
        {isLoading ? (
          <div style={{ padding: 16, fontSize: 13, color: 'var(--text-3, #8F959E)', textAlign: 'center' }}>加载中…</div>
        ) : rows.length === 0 ? (
          <EmptyHint arrow={false}>暂无班型交付数据</EmptyHint>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 4 }}>
            {rows.map((r) => {
              // 进度条按平均进度代理值渲染（出单=最高态），完成率=已完成/总数
              const pct = Math.round((r.avgProgress ?? 0) * 100);
              const completedPct = r.total > 0 ? Math.round((r.completed / r.total) * 100) : 0;
              return (
                <div key={r.classType} style={{ fontSize: 13 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ width: 64, color: 'var(--text-1, #1F2329)', fontWeight: 500 }}>{r.classType}</span>
                    <span style={{ color: 'var(--text-3, #8F959E)' }}>共 {r.total} 人</span>
                    <span style={{ marginLeft: 'auto' }}>
                      <Pill tone={completedPct >= 50 ? 'success' : completedPct > 0 ? 'primary' : 'default'}>
                        出单 {r.completed} / {r.total}
                      </Pill>
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, height: 18, background: 'var(--bg-2, #F4F5F7)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{
                        width: `${pct > 0 ? Math.max(2, pct) : 0}%`, height: '100%',
                        background: 'var(--primary, #3370FF)',
                      }} />
                    </div>
                    <span style={{ width: 48, textAlign: 'right', color: pct > 0 ? 'var(--text-1)' : 'var(--text-3)' }}>
                      {pct}%
                    </span>
                  </div>
                </div>
              );
            })}
            <div style={{ fontSize: 11, color: 'var(--text-3, #8F959E)', marginTop: 4 }}>
              进度为学员当前阶段加权代理值（出单=100%），非真实学习完成率
            </div>
          </div>
        )}
      </HomeCard>

      {/* 逾期任务数 */}
      <HomeCard title="逾期任务">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <StatCard
            label="逾期未完成任务"
            value={overdue}
            unit="项"
            valueColor={overdue > 0 ? 'danger' : 'success'}
            meta={`覆盖学员 ${totalStudents} 人`}
          />
          <div style={{ fontSize: 12, color: 'var(--text-3, #8F959E)', lineHeight: 1.6 }}>
            逾期口径：任务日期早于今天且仍未完成。数字偏高时需关注学员对接进度。
          </div>
        </div>
      </HomeCard>
    </div>
  );
}
